import { useRef, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit-react is in the parent dir
import JoditEditor from 'jodit-react';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore jodit is in the parent dir
import { IJodit } from 'jodit/esm/types';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { Button } from 'components/Button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { DrumkitPlatform } from 'contexts/sidebarStateContext';
import { Maybe } from 'types/UtilityTypes';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { copyToClipboard, createRichTextContent } from 'utils/copyToClipboard';
import { getJoditEditorConfig } from 'utils/getJoditEditorHelpers';

import { DraftRecipientFields } from '../common/DraftRecipientFields';
import { OutlookQuoteReplyActions } from '../common/OutlookQuoteReplyActions';
import { UseDraftRecipientsResult } from '../hooks/useDraftRecipients';

const COPIED_FEEDBACK_MS = 2000;

type BatchLTLDraftSectionProps = {
  draft: string;
  isRepEdited: boolean;
  drumkitPlatform: DrumkitPlatform;
  recipients: UseDraftRecipientsResult;
  isSharedMailbox: boolean;
  isOutlookReply: boolean;
  isSendingReply: boolean;
  onRepEdit: (value: string) => void;
  onResetToGenerated: () => void;
  onCopied: () => void;
  onReply: (draftBody: string, isSend: boolean) => void;
};

// Live batch reply: the editor, recipients, and the existing Outlook actions (Front copies).
export function BatchLTLDraftSection({
  draft,
  isRepEdited,
  drumkitPlatform,
  recipients,
  isSharedMailbox,
  isOutlookReply,
  isSendingReply,
  onRepEdit,
  onResetToGenerated,
  onCopied,
  onReply,
}: BatchLTLDraftSectionProps) {
  // Jodit instance, so Draft/Send/Copy use what is in the editor even before blur.
  const joditRef = useRef<Maybe<IJodit>>(null);
  const [hasCopied, setHasCopied] = useState(false);

  const getLatestDraft = (): string => {
    const editorValue = joditRef.current?.value;

    return typeof editorValue === 'string' ? editorValue : draft;
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(
      createRichTextContent(getLatestDraft()),
      { joditEditorRef: joditRef, wrapInHTML: true }
    );
    if (!success) {
      return;
    }

    setHasCopied(true);
    onCopied();
    setTimeout(() => setHasCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <Flex
      direction='col'
      gap='sm'
      className='w-full'
      data-testid='batch-ltl-draft'
    >
      <Flex justify='between' align='center' className='w-full'>
        <Flex align='center' gap='sm'>
          <Typography variant='h6' className='text-neutral-800'>
            Draft response
          </Typography>
          {isRepEdited && (
            <button
              type='button'
              className='text-xs text-info-600 underline-offset-2 hover:underline'
              onClick={onResetToGenerated}
            >
              Reset to generated
            </button>
          )}
        </Flex>
        <Button
          type='button'
          variant='ghost'
          className='h-6 w-6 border-none p-0'
          aria-label='Copy draft response'
          onClick={() => {
            if (!hasCopied) {
              void handleCopy();
            }
          }}
          buttonNamePosthog={ButtonNamePosthog.BatchLTLQuoteCopyToClipboard}
        >
          {hasCopied ? (
            <TooltipProvider>
              <Tooltip open={true}>
                <TooltipTrigger asChild>
                  <CheckIcon className='h-4 w-4' />
                </TooltipTrigger>
                <TooltipContent>Copied!</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <CopyIcon className='h-4 w-4' />
          )}
        </Button>
      </Flex>

      <DraftRecipientFields
        to={recipients.to}
        cc={recipients.cc}
        bcc={recipients.bcc}
        onToChange={recipients.setTo}
        onCcChange={recipients.setCc}
        onBccChange={recipients.setBcc}
      />

      <JoditEditor
        value={draft}
        config={{
          ...getJoditEditorConfig({ refToUpdateAfterInit: joditRef }),
          placeholder: 'Rates will appear here as shipments finish.',
          minHeight: 192,
        }}
        onBlur={(value: string) => {
          onRepEdit(value || getLatestDraft());
        }}
      />

      <OutlookQuoteReplyActions
        drumkitPlatform={drumkitPlatform}
        isSharedMailbox={isSharedMailbox}
        isOutlookReply={isOutlookReply}
        loadingDraftReply={isSendingReply}
        disabled={recipients.to.length === 0}
        onSend={() => onReply(getLatestDraft(), true)}
        onDraft={() => onReply(getLatestDraft(), false)}
        onCopy={() => void handleCopy()}
        draftButtonNamePosthog={ButtonNamePosthog.BatchLTLQuoteCreateDraftReply}
        addToCurrentDraftButtonNamePosthog={
          ButtonNamePosthog.BatchLTLQuoteAddReplyToCurrentDraft
        }
        copyButtonNamePosthog={ButtonNamePosthog.BatchLTLQuoteCopyToClipboard}
      />
    </Flex>
  );
}
